<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { WbSelect } from '@dh-io-owpi/shared/src/components/ssr/wb';
import { SecondLevelTarget } from '@dh-io-owpi/shared/src/components/SecondLevel';
import { useI18n } from '@dh-io-owpi/shared/src/plugins/i18n';
import { formatLabel, useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import { usePageStateStore } from '@/stores/pageState';
import { useTheme } from '@/stores/theme';
import { useLoaderData } from '@/composables/useLoaderData';
import TechDataTable from './components/TechDataTable.vue';
import AllTechDataModal from './components/AllTechDataModal.vue';
import {
  mapTechData,
  removeEmptyRows,
  removeEmptyRowsAndGroups,
  type LabeledTableData,
  type FuelFilter,
  mapAllTechData,
} from './utils/mapData';
import SPASection from '@/components/SPASection.vue';

const pageState = usePageStateStore();
const loaderData = useLoaderData();
const i18n = useI18n();
const theme = useTheme();

const { trackOnce } = useTrackingStore();
const selectedFuelIndex = ref(0);
const showAllTechDataModal = ref(false);

const fuelFilters = computed<FuelFilter[]>(() => {
  if (!loaderData.technicalDataSheet || !pageState.style.technicalDataSheetModels) return [];
  const filterKey = Object.keys(loaderData.technicalDataSheet.filters)[0];
  const fuelModelMap: Record<string, string[]> = {};

  for (const m of pageState.style.technicalDataSheetModels) {
    const fuel = loaderData.technicalDataSheet.models[m]?.filter[filterKey];
    if (!fuel) continue;
    (fuelModelMap[fuel] ??= []).push(m); // NOSONAR
  }

  return Object.entries(fuelModelMap).map(([label, models]) => ({ label, models }));
});

const initialData = computed<LabeledTableData[]>(() => {
  if (!loaderData.technicalDataSheet) {
    return [];
  }

  const mappedTechData = mapTechData(
    loaderData.technicalDataSheet,
    loaderData.config.techData.wltp,
    fuelFilters.value,
    i18n.t('ecoLabel.energyLabel') ?? '',
  );

  return removeEmptyRows(mappedTechData);
});

const allData = computed<LabeledTableData[]>(() => {
  if (!loaderData.technicalDataSheet) {
    return [];
  }

  const mappedAllTechData = mapAllTechData(
    loaderData.technicalDataSheet,
    loaderData.config.techData.wltp,
    fuelFilters.value,
    i18n.t('ecoLabel.energyLabel') ?? '',
  );
  return removeEmptyRowsAndGroups(mappedAllTechData);
});

function showModal() {
  showAllTechDataModal.value = true;

  trackOnce({
    name: 'open_modal',
    category: 'feature',
    action: 'tech_data|details|technical_data',
    label: 'all_technical_data.' + formatLabel(fuelFilters.value[selectedFuelIndex.value].label),
  });
}

watch(selectedFuelIndex, () => {
  trackOnce({
    name: 'show_more_details',
    category: 'feature',
    action: 'tech_data|fuel_type_selector|technical_data',
    label: formatLabel(fuelFilters.value[selectedFuelIndex.value].label),
  });
});
</script>

<template>
  <SPASection name="techData" title>
    <section class="owpi-spa-tech-data">
      <div v-if="fuelFilters.length > 1" class="_filter">
        <div class="_buttons">
          <p class="wb-text-l">{{ $t('techData.fuelType') }}</p>
          <div class="wbx-button-group">
            <button
              v-for="(f, i) of fuelFilters"
              :key="f.label"
              class="wbx-button wbx-button--large wbx-button--secondary"
              :class="{ 'wbx-button--selected': selectedFuelIndex === i }"
              @click="selectedFuelIndex = i"
            >
              {{ f.label }}
            </button>
          </div>
        </div>
        <WbSelect class="_dropdown">
          <span slot="label">{{ $t('techData.fuelType') }}:</span>
          <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label - workbench will add the label -->
          <select name="model" v-model="selectedFuelIndex">
            <option v-for="(f, i) of fuelFilters" :key="f.label" :value="i">{{ f.label }}</option>
          </select>
        </WbSelect>
      </div>
      <TechDataTable
        v-for="(tableData, i) of initialData"
        :key="tableData.label"
        class="_table"
        v-show="selectedFuelIndex === i"
        :data="tableData"
      />

      <button class="wbx-button wbx-button--secondary wbx-button--large _button" @click="showModal">
        {{ $t('techData.allTechData') }}
      </button>

      <SecondLevelTarget :brand="theme.brand" id="tech-data-modal">
        <AllTechDataModal
          v-for="(tableData, i) of allData"
          :key="tableData.label"
          :open="showAllTechDataModal && selectedFuelIndex === i"
          :data="tableData"
          :filterText="tableData.label"
          @close="showAllTechDataModal = false"
        />
      </SecondLevelTarget>
    </section>
  </SPASection>
</template>

<style lang="scss">
.owpi-spa-tech-data {
  > ._filter {
    margin-inline: auto;
    width: var(--wb-grid-width);

    > ._buttons p {
      color: var(--wb-color-text-main-default);
    }

    .wbx-button-group {
      margin-block: var(--wb-spacing-s);
      flex-wrap: wrap;
      .wbx-button {
        width: auto;
        flex: 0 1 auto;
        white-space: nowrap;
      }
    }
    ._dropdown {
      margin-bottom: var(--wb-spacing-s);
      display: none;
    }
    @include wb.breakpoint-to(mq3) {
      ._buttons {
        display: none;
      }
      ._dropdown {
        display: block;
      }
    }
  }

  > ._table {
    --inline-margin: calc((100% - var(--wb-grid-width)) / 2);
  }

  > ._button {
    margin-block: var(--wb-spacing-l);
    display: block;
    margin-inline: auto;
    @include wb.breakpoint-to(mq3) {
      width: var(--wb-grid-width);
    }
  }
}
</style>
